import { oktaAuth, oktaAuthFor, getLoginTenant, loginRequest, silentTokenRequest } from '../authConfig';
import { msalInstance, ensureMsalInitialized, authReady } from '../components/AuthProvider';
import { cachedSessionProvider, readCachedAuthState } from './authState';
import { isNative } from '../capacitor';
import { type AccountInfo } from '@azure/msal-browser';
import { isTestModeBypass } from './testMode';
import { navigateWhenReleased } from './navigationHold';
import {
  getNativeMicrosoftIdToken,
  hasStoredNativeMicrosoftSession,
  startNativeMicrosoftReauth,
} from './nativeMsAuth';

// How long to suppress a repeat interactive redirect after one was started. If the user
// returns from the provider still unable to acquire a token silently (e.g. a Conditional
// Access policy they can't satisfy), this stops us from redirecting again on the next API
// call — which would be an infinite bounce. Instead the request goes out token-less and
// fetchV2WithAuth ends the session, landing the user on the login screen. A genuine later
// attempt (past the cooldown) can redirect again.
const REAUTH_COOLDOWN_MS = 60_000;

// Where each provider's callback looks for the path to return the user to.
const MS_RETURN_TO_KEY = 'msalReturnTo';
const OKTA_RETURN_TO_KEY = 'oktaReturnTo';

function withinReauthCooldown(attemptKey: string): boolean {
  try {
    const lastAttempt = Number(sessionStorage.getItem(attemptKey)) || 0;
    return !!lastAttempt && Date.now() - lastAttempt < REAUTH_COOLDOWN_MS;
  } catch {
    // sessionStorage unavailable — the per-load in-progress flag is the only guard left.
    return false;
  }
}

// Stamp the attempt and record where the user was, so the interactive round-trip returns them
// there rather than to the home page.
function markReauthStarted(attemptKey: string, returnToKey: string): void {
  try {
    sessionStorage.setItem(attemptKey, String(Date.now()));
    sessionStorage.setItem(returnToKey, window.location.pathname + window.location.search);
  } catch { /* sessionStorage unavailable */ }
}

// Recover a Microsoft session whose token we can no longer obtain silently, by sending the
// user through an interactive redirect. Called whenever silent acquisition leaves us with NO
// usable token at all — deliberately not conditioned on the error class. MSAL signals an
// unrecoverable-silently session in more ways than InteractionRequiredAuthError: once the
// refresh token ages out it falls back to a hidden iframe against the Entra session cookie,
// and when browsers block or stall that iframe the failure surfaces as a BrowserAuthError
// (timed_out, iframe_closed_prematurely, block_iframe_reload). Treating only the former as
// recoverable left the latter to send a token-less request, which the backend answers with a
// 401 the frontend can't classify — the user stays nominally signed in while their role
// silently degrades (WIPE-946). Guarded two ways: a per-load flag (one redirect at a time
// across concurrent v2 calls) and the sessionStorage cooldown above (breaks redirect loops).
//
// Without an account, MSAL no longer holds the session at all — its cache was evicted or cleared
// while the app's own record of the sign-in survived — so there is nothing to acquire a token
// for, and recovery is a fresh login instead, hinted with the recorded email so Entra can reuse
// its session cookie rather than show the account picker.
let msReauthInProgress = false;
function triggerMicrosoftReauth(account?: AccountInfo): void {
  if (msReauthInProgress || isTestModeBypass()) return;
  if (withinReauthCooldown('msReauthAt')) return;

  msReauthInProgress = true;
  markReauthStarted('msReauthAt', MS_RETURN_TO_KEY);

  // Held off while a live chat would not survive the navigation (see navigationHold).
  navigateWhenReleased(() => {
    // Navigates away; the promise only settles (rejects) if the redirect can't even start
    // (e.g. interaction_in_progress) — in which case clear the flag so a later call retries.
    const redirect = account
      ? msalInstance.acquireTokenRedirect({ ...silentTokenRequest, account })
      : msalInstance.loginRedirect({ ...loginRequest, loginHint: readCachedAuthState()?.user?.email || undefined });
    redirect.catch(() => {
      msReauthInProgress = false;
    });
  });
}

// The same recovery for a native (Capacitor) Microsoft session, which MSAL knows nothing
// about — it was established by the code+PKCE flow in nativeMsAuth, so triggerMicrosoftReauth
// above can't reach it: there is no AccountInfo to pass and acquireTokenRedirect doesn't work
// under the WebView schemes. Reached only once that session's own refresh token is spent or
// revoked, since until then getNativeMicrosoftIdToken renews silently.
//
// Guarded like the other two: one round-trip at a time per load, plus the sessionStorage
// cooldown, so a session Entra won't renew can't put the app in a redirect loop.
let nativeMsReauthInProgress = false;
function triggerNativeMicrosoftReauth(): void {
  if (nativeMsReauthInProgress || isTestModeBypass()) return;
  if (withinReauthCooldown('nativeMsReauthAt')) return;

  nativeMsReauthInProgress = true;
  markReauthStarted('nativeMsReauthAt', MS_RETURN_TO_KEY);

  navigateWhenReleased(() => {
    // On Android this opens a Custom Tab and the app keeps running, so — unlike the web
    // redirect paths — control returns here either way; clear the flag when the round-trip
    // couldn't start so a later call can retry.
    startNativeMicrosoftReauth().then((started) => {
      if (!started) nativeMsReauthInProgress = false;
    });
  });
}

// The same recovery for an Okta session that can no longer be renewed silently — the state a
// session established without `offline_access` reaches an hour in, and the one any session
// reaches once its refresh token falls outside Okta's 7-day inactivity window. Okta normally
// still holds its own session, so the round-trip is a bounce with no prompt.
//
// Without this an Okta-only user has no way back at all: the app keeps rendering as signed in
// on the strength of a token it can no longer use, while every v2 call goes out bare and the
// caller's role silently degrades. Clearing site data was the only escape.
//
// Returns whether a redirect is now pending. False means recovery is off the table — test mode,
// or the cooldown after an attempt that came back no better — and the caller has to end the
// session instead.
let oktaReauthInProgress = false;
function triggerOktaReauth(): boolean {
  if (oktaReauthInProgress) return true;
  if (isTestModeBypass() || withinReauthCooldown('oktaReauthAt')) return false;

  oktaReauthInProgress = true;
  markReauthStarted('oktaReauthAt', OKTA_RETURN_TO_KEY);

  // Re-authenticate against the tenant that issued the dead session, not whichever one logins
  // start at, so a user who arrived through the other tenant's tile lands back in it.
  const tenant = getLoginTenant();

  navigateWhenReleased(() => {
    oktaAuthFor(tenant).signInWithRedirect().catch(() => {
      oktaReauthInProgress = false;
    });
  });
  return true;
}

// Entry point for AuthProvider, which hears from the Okta SDK the moment a background renewal
// fails — well before the next v2 call would notice the token is gone.
export function recoverOktaSession(): boolean {
  return triggerOktaReauth();
}

// Whether an interactive redirect has been started (or queued behind a live chat) and will
// replace the session shortly, so a token-less 401 in the meantime is not a reason to end it.
//
// The native round-trip has to count. On Android it opens a Custom Tab and leaves the app running,
// so the request that triggered it stays in flight and comes back 401 — and ending the session on
// that 401 would sign the user out from under a re-authentication that is about to succeed.
function reauthPending(): boolean {
  return msReauthInProgress || oktaReauthInProgress || nativeMsReauthInProgress;
}

// Auth headers for the FranchiseApi v2 backend, which identifies the caller from a
// verified token (Okta or Microsoft) — not a trusted header. A user may hold sessions
// with BOTH providers at once and either token may be expired, so we send whatever we
// have and let the backend use whichever verifies: the Okta token in the standard
// Authorization header, and the Microsoft (Entra) ID token in X-Microsoft-Token.
//
// For Microsoft we send the ID TOKEN, not the Graph access token: the backend checks
// `aud` equals our app's client id, which only the ID token satisfies.
//
// Shared by every client that calls the v2 API (franchiseApi, adminApi, syncApi) so the
// auth behavior — including expiry-driven refresh, below — stays identical across them.
export async function getV2AuthHeaders(forceRefresh = false): Promise<Record<string, string>> {
  const headers: Record<string, string> = {};

  // Wait for the initial auth determination to settle before reading tokens. A component
  // that fires a v2 call from its mount effect can run before AuthProvider has loaded the
  // session; without this the call would go out with no Authorization header and draw a
  // spurious 401. authReady() self-resolves after a safety timeout, so this never hangs.
  await authReady();

  // Whether Okta ever seated a session in this browser, which is not the same as holding a
  // usable token — it is what separates a session needing recovery from a visitor who was never
  // signed in. The token store alone can't answer that: the SDK renews tokens in the background
  // and deletes them when renewal fails, so a lapsed session usually presents as an empty store.
  // The app's own record of the signed-in provider outlives that, and is cleared only when the
  // app itself ends the session. The store is still consulted, before any renewal here, for a
  // session whose record was lost.
  let hasOktaSession = cachedSessionProvider() === 'okta';

  try {
    const stored = await oktaAuth.tokenManager.getTokens();
    hasOktaSession ||= !!(stored.accessToken || stored.idToken || stored.refreshToken);

    // Mint a fresh Okta access token from the refresh token, replacing the cached one in the
    // token manager so getTokens() below returns the new one. Done when forced, and whenever
    // the access token is gone but a refresh token remains: the SDK deletes an access token
    // whose background renewal failed for any reason, but keeps the refresh token unless Okta
    // rejected it, so a transient failure is still renewable here without a redirect.
    const renewing = forceRefresh || (!stored.accessToken && !!stored.refreshToken);
    if (renewing) await oktaAuth.tokenManager.renew('accessToken');
    const tokens = renewing ? await oktaAuth.tokenManager.getTokens() : stored;
    const oktaToken = tokens.accessToken?.accessToken;
    if (oktaToken) headers.Authorization = `Bearer ${oktaToken}`;
  } catch {
    // No Okta session, or renewal failed (e.g. refresh token expired) — omit it. A renewal
    // that throws deliberately leaves the header unset rather than falling back to the
    // expired token it just failed to replace, so the outcome below reads as no credentials.
  }

  let msAccount: AccountInfo | undefined;
  try {
    // MSAL must be initialized before getAllAccounts/acquireTokenSilent, or it throws
    // uninitialized_public_client_application. This call can run before/independently of
    // AuthProvider's own init, so guarantee it here.
    await ensureMsalInitialized();
    // Prefer the active account (set at login / on auth restore) so silent acquisition
    // resolves the same account MSAL cached tokens under; fall back to the first account.
    msAccount = msalInstance.getActiveAccount() ?? msalInstance.getAllAccounts()[0];
    if (msAccount) {
      // forceRefresh bypasses MSAL's token cache and uses the refresh token to mint a
      // fresh ID token — the recovery path when the backend reports the cached one expired.
      const result = await msalInstance.acquireTokenSilent({ account: msAccount, ...silentTokenRequest, forceRefresh });
      if (result.idToken) headers['X-Microsoft-Token'] = result.idToken;
    }
  } catch {
    // Silent acquisition failed. Don't branch on the error class here — whether recovery is
    // needed depends on what we ENDED UP with, which is decided below once the Okta fallback
    // is also known.
  }

  // A Capacitor build's Microsoft session is established by the code+PKCE flow in nativeMsAuth,
  // not by MSAL, so the block above finds no account and produces nothing — which left every v2
  // call from a native Microsoft session going out with no credential at all, and the backend
  // answering "Missing Authorization header" from the very first request. That flow's ID token
  // carries the same `aud` (our client id) the backend checks, so it satisfies the same
  // verification MSAL's does.
  //
  // Renews from its own refresh token when the session has expired, so this is also the native
  // equivalent of acquireTokenSilent above; it returns null only once renewal is impossible,
  // which is what the recovery below keys on. `forceRefresh` needs no special handling — the
  // session refreshes on expiry whether or not a caller asked, and a token that is still live is
  // the same token a forced refresh would have left in place.
  if (!headers['X-Microsoft-Token']) {
    const nativeIdToken = await getNativeMicrosoftIdToken();
    if (nativeIdToken) headers['X-Microsoft-Token'] = nativeIdToken;
  }

  // The request is about to go out with no credentials of any kind, so silent renewal can no
  // longer produce a token for whichever provider holds the session. Recover interactively
  // rather than sending a token-less request and letting the caller's role silently degrade.
  //
  // Keyed on the absence of BOTH tokens, not on how acquisition failed: a user holding a valid
  // token for either provider is still authenticated and must not be bounced anywhere, while
  // one with neither has nothing left to authenticate with regardless of the error class.
  //
  // A visitor with no session at all is left alone — there is nothing to recover, and
  // AuthProvider is already rendering the login screen.
  //
  // The last branch is for a Microsoft session MSAL no longer holds an account for, recovered by a
  // fresh login. It is gated on not being native because MSAL's redirect cannot run in the
  // Capacitor WebView — the reason the native flow exists at all. Gating it on the absence of a
  // native session instead would not do: a native session that has been cleared, or one from a
  // build whose tokens lived elsewhere, still leaves the cached provider saying 'microsoft' and
  // would send the user into a redirect the WebView can't complete.
  if (!headers.Authorization && !headers['X-Microsoft-Token']) {
    if (msAccount) triggerMicrosoftReauth(msAccount);
    else if (await hasStoredNativeMicrosoftSession()) triggerNativeMicrosoftReauth();
    else if (hasOktaSession) triggerOktaReauth();
    else if (cachedSessionProvider() === 'microsoft' && !isNative) triggerMicrosoftReauth();
  }

  return headers;
}

/**
 * The credentials a WebSocket connection to the v2 backend has to carry.
 *
 * Derived from getV2AuthHeaders rather than read from the token stores directly, so a socket
 * authenticates with exactly the same session a v2 HTTP call does — including the wait for auth
 * init to settle and the interactive recovery a Microsoft account with no usable token needs.
 *
 * Both parameter names are provider-agnostic: the backend keys its issuer registry on each
 * token's own `iss`, so neither name says anything about who issued it, and it takes the first
 * that verifies. `token` therefore has to carry whichever credential we hold — API Gateway
 * refuses a handshake that omits that parameter before the authorizer even runs — so a
 * Microsoft-only session puts its token there, and `altToken` is used only for the second
 * credential when the user holds sessions with both providers.
 */
export interface WsCredentials {
  token?: string;
  altToken?: string;
}

// `forceRefresh` is here for the same reason it is on getV2AuthHeaders, but a caller has to
// decide to use it up front: a handshake can't recover after the fact the way fetchV2WithAuth
// replays a 401, because a browser reports a refused upgrade without a status or a body.
export async function getV2WsCredentials(forceRefresh = false): Promise<WsCredentials> {
  const headers = await getV2AuthHeaders(forceRefresh);
  const [token, altToken] = [headers.Authorization?.replace(/^Bearer /, ''), headers['X-Microsoft-Token']]
    .filter((value): value is string => !!value);

  const credentials: WsCredentials = {};
  if (token) credentials.token = token;
  if (altToken) credentials.altToken = altToken;
  return credentials;
}

// Dispatched when the session is dead and nothing will revive it: the backend reports
// { code: 'invalid_token' }, or a request is still token-less after a forced refresh with no
// interactive redirect pending, or AuthProvider can't recover a session whose silent renewal
// failed. AuthProvider listens and drops the user to the login screen. Deliberately NOT fired
// for other 401s (non-JSON, or an expired token a refresh didn't fix): those are dominated by
// benign timing (a call racing auth init), which getV2AuthHeaders waits out, so treating them
// as a dead session would log healthy users out. Such 401s just propagate to the caller.
export const AUTH_UNAUTHORIZED_EVENT = 'auth:unauthorized';

// The E2E test bypass (see AuthProvider.checkAuth) signs in a mock user with no real
// Okta/Microsoft tokens, so v2 calls come back 401. Never drop that session to the login
// screen — doing so unmounts every page the E2E suite drives.
function signalUnauthorized(): void {
  if (isTestModeBypass()) return;
  window.dispatchEvent(new Event(AUTH_UNAUTHORIZED_EVENT));
}

// The `code` of a 401 from the v2 backend, read from a clone so the caller can still consume
// the body. Undefined for a non-JSON 401.
async function unauthorizedCode(response: Response): Promise<string | undefined> {
  try {
    return (await response.clone().json())?.code;
  } catch {
    return undefined;
  }
}

// Fetch a v2 endpoint with auth headers, transparently recovering from an expired token.
// The backend answers an expired (but otherwise valid) token with 401 { code:
// 'token_expired' }; we then mint fresh tokens (forceRefresh) and replay the request once.
// A malformed/forged/dead session returns code 'invalid_token' instead: not retried (a
// refresh wouldn't help) and fired as AUTH_UNAUTHORIZED_EVENT to trigger reauth. A request
// that still carries no credentials after a forced refresh, with no redirect pending to
// replace them, is treated the same way. Any other 401 just propagates to the caller. `init.headers` from the caller win over the
// auth/content-type defaults except for the headers we own here.
export async function fetchV2WithAuth(url: string, init: RequestInit = {}): Promise<Response> {
  const send = async (forceRefresh: boolean): Promise<Response> => {
    const authHeaders = await getV2AuthHeaders(forceRefresh);
    return fetch(url, {
      ...init,
      headers: {
        'Content-Type': 'application/json',
        'x-api-key': import.meta.env.VITE_FRANCHISE_API_KEY || '',
        ...(init.headers as Record<string, string> | undefined),
        ...authHeaders,
      },
    });
  };

  const response = await send(false);
  if (response.status !== 401) return response;

  const code = await unauthorizedCode(response);

  // Dead/forged session: a refresh won't help. Trigger reauth and return the 401.
  if (code === 'invalid_token') {
    signalUnauthorized();
    return response;
  }

  // We sent no token at all, because silent acquisition produced none. getV2AuthHeaders has
  // usually already started an interactive redirect, but replay once with forceRefresh first:
  // that bypasses MSAL's cache and can succeed on a transient iframe failure, sparing the user
  // the round-trip.
  //
  // If the replay is still token-less and no redirect is coming — nothing left to recover, or
  // the cooldown after an attempt that didn't help — the session is over. End it, so the user
  // lands on the login screen rather than in an app whose every call fails.
  //
  // A native Microsoft session needs no exception here any more. It used to be token-less on every
  // v2 call by construction, so ending it would have signed every native user out on their first
  // call; now it sends an ID token like any other, and reauthPending() covers the window where it
  // is being re-established. Reaching this point on native means the session really is finished.
  if (code === 'no_token') {
    const retried = await send(true);
    if (
      retried.status === 401 &&
      !reauthPending() &&
      (await unauthorizedCode(retried)) === 'no_token'
    ) {
      signalUnauthorized();
    }
    return retried;
  }

  // Expired token: mint fresh tokens and replay once. Anything else propagates unchanged.
  if (code !== 'token_expired') return response;

  return send(true);
}
